import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./baseQuery";

/**
 * Grup müzakereleri (yönetici): liste, tek tek ekleme (sihirbaz), Excel'den
 * toplu ekleme, silme, günü gelmiş müzakerede kardeşi katıldı/katılmadı
 * işaretleme ve değerlendirme (soruları düzenle, başlat, sonuçlar).
 * Kütüphane uçları libraryApi.js'te bu dilime eklenir.
 */
export const muzakereApi = createApi({
  reducerPath: "panelMuzakere",
  baseQuery: axiosBaseQuery(),
  refetchOnFocus: true,
  tagTypes: ["GroupMuzakeres", "ManagerAssessment", "AssessmentResults", "Library"],
  endpoints: (builder) => ({
    // Sayfalı liste (20'şer, önce bugün ve yaklaşanlar, sonra geçmişler);
    // q başlıkta arar, yanıtta hasMore döner. Her sayfa ayrı önbellekte.
    listGroupMuzakeres: builder.query({
      query: ({ groupId, q, page = 1 }) => ({
        url: "/api/panel/muzakeres/list-group",
        method: "POST",
        data: { groupId, page, ...(q ? { q } : {}) },
      }),
      providesTags: (result, error, { groupId }) => [{ type: "GroupMuzakeres", id: groupId }],
    }),
    bulkCreateMuzakeres: builder.mutation({
      query: ({ groupId, rows }) => ({
        url: "/api/panel/muzakeres/bulk-create",
        method: "POST",
        data: { groupId, rows },
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: "GroupMuzakeres", id: groupId }],
    }),
    // Mobil uç (yalnız protect): mobildeki yönetici işaretlemesiyle aynı.
    setMemberMuzakereDone: builder.mutation({
      query: ({ muzakereId, memberId, done, today }) => ({
        url: "/api/muzakeres/set-member-done",
        method: "POST",
        data: { muzakereId, memberId, done, today },
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: "GroupMuzakeres", id: groupId }],
    }),
    // Mobil uç (yalnız protect): tek müzakere ya da tekrarlı seri; isteğe
    // bağlı sorular ve paket (sorulu müzakere tekrarlanmaz). Gövde:
    // muzakereWizardRules.buildCreateBody.
    createMuzakere: builder.mutation({
      query: (body) => ({ url: "/api/muzakeres/create", method: "POST", data: body }),
      invalidatesTags: (result, error, { groupId }) => [{ type: "GroupMuzakeres", id: groupId }],
    }),
    // Yönetici: sorular doğru cevaplarla ve paket bağı (soru düzenleme için).
    getManagerAssessment: builder.query({
      query: ({ muzakereId }) => ({
        url: "/api/muzakeres/assessment",
        method: "POST",
        data: { muzakereId },
      }),
      providesTags: (result, error, { muzakereId }) => [{ type: "ManagerAssessment", id: muzakereId }],
    }),
    // Soruları yazar (boş dizi: sorusuz); başlamış değerlendirmede sunucu reddeder.
    setAssessment: builder.mutation({
      query: ({ muzakereId, questions, packageId }) => ({
        url: "/api/muzakeres/set-assessment",
        method: "POST",
        data: { muzakereId, questions, ...(packageId ? { packageId } : {}) },
      }),
      invalidatesTags: (result, error, { muzakereId, groupId }) => [
        { type: "GroupMuzakeres", id: groupId },
        { type: "ManagerAssessment", id: muzakereId },
      ],
    }),
    // dryRun: başlatmadan kuralları denetler, son 60 günde sorulan soru varsa
    // warning döner (onay için). dryRun'da önbellek tazelenmez.
    startAssessment: builder.mutation({
      query: ({ muzakereId, dryRun }) => ({
        url: "/api/muzakeres/start-assessment",
        method: "POST",
        data: { muzakereId, ...(dryRun ? { dryRun: true } : {}) },
      }),
      invalidatesTags: (result, error, { muzakereId, groupId, dryRun }) =>
        dryRun
          ? []
          : [
              { type: "GroupMuzakeres", id: groupId },
              { type: "ManagerAssessment", id: muzakereId },
              { type: "AssessmentResults", id: muzakereId },
            ],
    }),
    // Yönetici: gönderenler (puan, cevaplar), göndermeyenler, sorular.
    getAssessmentResults: builder.query({
      query: ({ muzakereId }) => ({
        url: "/api/muzakeres/assessment-results",
        method: "POST",
        data: { muzakereId },
      }),
      providesTags: (result, error, { muzakereId }) => [{ type: "AssessmentResults", id: muzakereId }],
    }),
    // scope "following": tekrarlanan serinin bu ve sonraki müzakereleri de silinir.
    deleteMuzakere: builder.mutation({
      query: ({ muzakereId, scope, today }) => ({
        url: "/api/panel/muzakeres/delete",
        method: "POST",
        data: { muzakereId, ...(scope ? { scope } : {}), ...(today ? { today } : {}) },
      }),
      invalidatesTags: (result, error, { groupId }) => [{ type: "GroupMuzakeres", id: groupId }],
    }),
  }),
});

export const {
  useListGroupMuzakeresQuery,
  useBulkCreateMuzakeresMutation,
  useDeleteMuzakereMutation,
  useSetMemberMuzakereDoneMutation,
  useCreateMuzakereMutation,
  useGetManagerAssessmentQuery,
  useSetAssessmentMutation,
  useStartAssessmentMutation,
  useGetAssessmentResultsQuery,
} = muzakereApi;
